import React, { useState, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import EncaissementService from "../../Services/Encaissement";
import DecaissementService from "../../Services/Decaissement";
import RapportX from "../../Services/RapportX";
import {
  Card,
  CardContent,
  Typography,
  Box,
  Button,
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Paper,
  Skeleton,
  useTheme,
  Container,
  Chip,
  Divider,
  Stack,
  IconButton,
  Tooltip,
  CircularProgress,
  Avatar,
  alpha,
  Badge,
  Fade,
  Zoom,
  LinearProgress,
  Alert
} from "@mui/material";
import {
  AccountBalanceWallet,
  ArrowUpward,
  ArrowDownward,
  ListAlt,
  FilterList,
  Autorenew,
  Clear,
  Refresh,
  TrendingUp,
  TrendingDown,
  Receipt,
  DateRange,
  CheckCircle,
  Warning,
  Info,
  Speed,
  ShowChart,
  AddCircle,
  History,
  Download,
  Print,
  NotificationsActive,
  Settings,
  Visibility,
  Dashboard
} from "@mui/icons-material";
import { green, red, blue, orange, purple, teal } from "@mui/material/colors";
import { motion, AnimatePresence } from "framer-motion";
import ExcelTable from "../../Components/Tables/VaultTable";

// Animation variants
const fadeInUp = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: -20 }
};

const staggerContainer = {
  animate: {
    transition: {
      staggerChildren: 0.1
    }
  }
};

const pulseAnimation = {
  initial: { scale: 1 },
  animate: { 
    scale: [1, 1.05, 1],
    transition: { 
      duration: 2,
      repeat: Infinity,
      repeatType: "reverse"
    }
  }
};

const VaultPage = () => {
  const theme = useTheme();
  const [rapportXValues, setRapportXValues] = useState({});
  const [totalRapportX, setTotalRapportX] = useState(0);
  const [encaissements, setEncaissements] = useState([]);
  const [decaissements, setDecaissements] = useState([]);
  const restaurantId = localStorage.getItem("restaurantId");

  const [dateDebut, setDateDebut] = useState("");
  const [dateFin, setDateFin] = useState("");
  const [filterType, setFilterType] = useState("");
  const [filteredData, setFilteredData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [autoRefresh, setAutoRefresh] = useState(false);
  const [refreshInterval, setRefreshInterval] = useState(30);
  const [hoveredCard, setHoveredCard] = useState(null);
  const [lastUpdate, setLastUpdate] = useState(new Date());

  // Fetch all data
  const fetchAllData = async () => {
    setIsRefreshing(true);
    try {
      const [encaissementsData, decaissementsData, allRapportX] = await Promise.all([
        EncaissementService.getAllEncaissements(),
        DecaissementService.getAllDecaissements(),
        RapportX.getAllRapportX(restaurantId)
      ]);
      
      setEncaissements(encaissementsData);
      setDecaissements(decaissementsData);
      
      // Process RapportX data
      const now = new Date();
      const currentMonth = now.getMonth();
      const currentYear = now.getFullYear();

      const filteredRapportX = allRapportX.filter(item => {
        const itemDate = new Date(item.date);
        return (
          itemDate.getMonth() === currentMonth &&
          itemDate.getFullYear() === currentYear
        );
      });

      const mappedValues = filteredRapportX.reduce((acc, item) => {
        acc[item.date] = item.value;
        return acc;
      }, {});

      const total = filteredRapportX.reduce((sum, item) => {
        return sum + Number(item.value);
      }, 0);

      setRapportXValues(mappedValues);
      setTotalRapportX(total);
      setLastUpdate(new Date());
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
      setIsRefreshing(false);
    }
  };

  // Initial data fetch
  useEffect(() => {
    fetchAllData();
  }, [restaurantId]);

  // Auto-refresh setup
  useEffect(() => {
    let intervalId;
    
    if (autoRefresh && restaurantId) {
      intervalId = setInterval(() => {
        fetchAllData();
      }, refreshInterval * 1000);
    }
    
    return () => {
      if (intervalId) clearInterval(intervalId);
    };
  }, [autoRefresh, refreshInterval, restaurantId]);

  const combinedData = useMemo(() => [
    ...encaissements
      .filter(e => e.restaurantId === restaurantId)
      .map(e => ({ ...e, category: "ENCAISSEMENT" })),
    ...decaissements
      .filter(d => d.restaurantId === restaurantId)
      .map(d => ({ ...d, category: "DECAISSEMENT" }))
  ], [encaissements, decaissements, restaurantId]);

  useEffect(() => {
    const filtered = combinedData.filter(item => {
      const itemDate = new Date(item.date);
      const start = dateDebut ? new Date(dateDebut) : null;
      const end = dateFin ? new Date(dateFin) : null;
      
      return (!start || itemDate >= start) && 
             (!end || itemDate <= end) && 
             (!filterType || item.category === filterType);
    });
    
    setFilteredData(filtered);
  }, [combinedData, dateDebut, dateFin, filterType]);

  const now = new Date();
  const firstDayOfMonth = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), 1));
  const lastDayOfMonth = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() + 1, 0));
  lastDayOfMonth.setUTCHours(23, 59, 59, 999);

  const totalEncaissements = (Array.isArray(encaissements) ? encaissements : [])
    .filter(e => 
        e.restaurantId === restaurantId && 
        e.date && new Date(e.date) >= firstDayOfMonth && new Date(e.date) <= lastDayOfMonth
    ) 
    .reduce((sum, e) => sum + (e.value || 0), 0);

  const totalDecaissements = (Array.isArray(decaissements) ? decaissements : [])
    .filter(d => 
        d.restaurantId === restaurantId && 
        d.date && new Date(d.date) >= firstDayOfMonth && new Date(d.date) <= lastDayOfMonth
    ) 
    .reduce((sum, d) => sum + (d.value || 0), 0);

  const solde = totalEncaissements - totalDecaissements; // Changed to subtraction for correct balance
  const variance = totalRapportX - solde;
  const variancePercentage = totalRapportX ? ((variance / totalRapportX) * 100).toFixed(2) : 0;

  const clearFilters = () => {
    setDateDebut("");
    setDateFin("");
    setFilterType("");
  };

  const toggleAutoRefresh = () => {
    setAutoRefresh(!autoRefresh);
  };

  const handleIntervalChange = (e) => {
    const value = parseInt(e.target.value);
    if (value >= 5) {
      setRefreshInterval(value);
    }
  };

  const formatNumber = (num) => {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'TND',
      minimumFractionDigits: 3,
      maximumFractionDigits: 3
    }).format(num || 0);
  };

  const formatCompact = (num) => {
    return new Intl.NumberFormat('fr-FR', {
      notation: 'compact',
      compactDisplay: 'short',
      maximumFractionDigits: 1
    }).format(num || 0);
  };

  if (!restaurantId) {
    return (
      <Container maxWidth="xl" sx={{ p: 4, minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Zoom in={true}>
          <Paper
            elevation={3}
            sx={{
              p: 6,
              borderRadius: 4,
              textAlign: 'center',
              background: 'linear-gradient(135deg, #FFFFFF 0%, #F8F9FA 100%)',
              border: '1px solid #E0E0E0',
              maxWidth: 500
            }}
          >
            <AccountBalanceWallet sx={{ fontSize: 80, color: '#9AA6B2', mb: 2 }} />
            <Typography variant="h4" sx={{ color: '#1A2B3C', fontWeight: 700, mb: 1 }}>
              Aucun restaurant sélectionné
            </Typography>
            <Typography variant="body1" sx={{ color: '#5F6B7A' }}>
              Veuillez sélectionner un restaurant pour voir la trésorerie
            </Typography>
          </Paper>
        </Zoom>
      </Container>
    );
  }

  return (
    <Box sx={{ 
      p: 4, 
      background: 'linear-gradient(145deg, #F5F7FA 0%, #E8ECF0 100%)',
      minHeight: '100vh',
      position: 'relative',
      width: '100%',
      overflowX: 'hidden'
    }}>
      {/* Background Decoration */}
      <Box sx={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'radial-gradient(circle at 50% 50%, rgba(33,150,243,0.03) 0%, transparent 50%)',
        pointerEvents: 'none',
        zIndex: 0
      }} />

      <Container maxWidth="xl" sx={{ position: 'relative', zIndex: 1 }}>
        {/* Header Section */}
        <motion.div
          initial="initial"
          animate="animate"
          variants={staggerContainer}
        >
          {/* Main Header Card */}
          <motion.div variants={fadeInUp}>
            <Paper
              elevation={0}
              sx={{
                mb: 4,
                background: 'linear-gradient(135deg, #1E2A3A 0%, #2C3E50 100%)',
                borderRadius: 4,
                border: '1px solid rgba(255,255,255,0.1)',
                overflow: 'hidden',
                position: 'relative',
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  top: 0,
                  left: '-100%',
                  width: '100%',
                  height: '100%',
                  background: 'linear-gradient(120deg, rgba(255,255,255,0) 0%, rgba(255,255,255,0.1) 50%, rgba(255,255,255,0) 100%)',
                  transition: 'left 0.6s',
                },
                '&:hover::before': {
                  left: '100%'
                }
              }}
            >
              <CardContent sx={{ p: 4 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                    <Zoom in={true} style={{ transitionDelay: '200ms' }}>
                      <Avatar
                        sx={{
                          width: 80,
                          height: 80,
                          background: 'linear-gradient(135deg, #FFD700, #FFA500)',
                          boxShadow: '0 10px 20px rgba(255,215,0,0.3)',
                        }}
                      >
                        <AccountBalanceWallet sx={{ fontSize: 48, color: '#1E2A3A' }} />
                      </Avatar>
                    </Zoom>
                    <Box>
                      <Typography 
                        variant="h3" 
                        sx={{ 
                          fontWeight: 800,
                          background: 'linear-gradient(45deg, #FFFFFF 30%, #FFD700 90%)',
                          WebkitBackgroundClip: 'text',
                          WebkitTextFillColor: 'transparent',
                          mb: 1,
                        }}
                      >
                        Gestion de Trésorerie
                      </Typography>
                      <Typography variant="h6" sx={{ color: '#B0BEC5', fontWeight: 400 }}>
                        Suivi en temps réel des flux financiers • Dernière mise à jour: {lastUpdate.toLocaleTimeString()}
                      </Typography>
                    </Box>
                  </Box>
                  
                  <Box sx={{ display: 'flex', gap: 2 }}>
                    <Tooltip title="Télécharger le rapport" arrow>
                      <IconButton
                        sx={{
                          bgcolor: 'rgba(255,255,255,0.1)',
                          color: '#FFFFFF',
                          '&:hover': { bgcolor: 'rgba(255,255,255,0.2)' }
                        }}
                      >
                        <Download />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Imprimer" arrow>
                      <IconButton
                        sx={{
                          bgcolor: 'rgba(255,255,255,0.1)',
                          color: '#FFFFFF',
                          '&:hover': { bgcolor: 'rgba(255,255,255,0.2)' }
                        }}
                      >
                        <Print />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Paramètres" arrow>
                      <IconButton
                        sx={{
                          bgcolor: 'rgba(255,255,255,0.1)',
                          color: '#FFFFFF',
                          '&:hover': { bgcolor: 'rgba(255,255,255,0.2)' }
                        }}
                      >
                        <Settings />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </Box>

                {/* Refresh Controls */}
                <Box sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: 2, 
                  mt: 3,
                  pt: 3,
                  borderTop: '1px solid rgba(255,255,255,0.1)'
                }}>
                  <Tooltip title="Rafraîchir maintenant">
                    <Button
                      variant="contained"
                      onClick={fetchAllData}
                      disabled={isRefreshing}
                      startIcon={isRefreshing ? <CircularProgress size={20} color="inherit" /> : <Refresh />}
                      sx={{
                        bgcolor: 'rgba(255,255,255,0.15)',
                        color: '#FFFFFF',
                        '&:hover': { bgcolor: 'rgba(255,255,255,0.25)' }
                      }}
                    >
                      {isRefreshing ? 'Rafraîchissement...' : 'Rafraîchir'}
                    </Button>
                  </Tooltip>

                  <Tooltip title={autoRefresh ? "Désactiver l'auto-refresh" : "Activer l'auto-refresh"}>
                    <Button
                      variant={autoRefresh ? "contained" : "outlined"}
                      onClick={toggleAutoRefresh}
                      startIcon={<Autorenew />}
                      sx={{
                        borderColor: 'rgba(255,255,255,0.3)',
                        color: '#FFFFFF',
                        bgcolor: autoRefresh ? 'rgba(255,215,0,0.2)' : 'transparent',
                        '&:hover': {
                          borderColor: '#FFD700',
                          bgcolor: autoRefresh ? 'rgba(255,215,0,0.3)' : 'rgba(255,255,255,0.05)'
                        }
                      }}
                    >
                      Auto-refresh
                    </Button>
                  </Tooltip>

                  {autoRefresh && (
                    <Fade in={true}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="body2" sx={{ color: '#B0BEC5' }}>
                          Intervalle:
                        </Typography>
                        <TextField
                          size="small"
                          type="number"
                          value={refreshInterval}
                          onChange={handleIntervalChange}
                          inputProps={{ min: 5, max: 300 }}
                          sx={{
                            width: 80,
                            '& .MuiInputBase-root': {
                              color: '#FFFFFF',
                              bgcolor: 'rgba(255,255,255,0.1)',
                              borderRadius: 2
                            },
                            '& .MuiOutlinedInput-notchedOutline': {
                              borderColor: 'rgba(255,255,255,0.2)'
                            },
                            '&:hover .MuiOutlinedInput-notchedOutline': {
                              borderColor: '#FFD700'
                            }
                          }}
                        />
                        <Typography variant="body2" sx={{ color: '#B0BEC5' }}>s</Typography>
                      </Box>
                    </Fade>
                  )}
                </Box>
              </CardContent>
            </Paper>
          </motion.div>

          {/* Stats Cards */}
          <Grid container spacing={3} sx={{ mb: 4 }}>
            {/* Encaissements Card */}
            <Grid item xs={12} sm={6} md={3}>
              <motion.div variants={fadeInUp}>
                <Card
                  onMouseEnter={() => setHoveredCard('encaissements')}
                  onMouseLeave={() => setHoveredCard(null)}
                  sx={{
                    height: '100%',
                    borderRadius: 3,
                    background: 'linear-gradient(135deg, #FFFFFF 0%, #F8F9FA 100%)',
                    border: `1px solid ${alpha(green[500], 0.2)}`,
                    transition: 'all 0.3s ease',
                    transform: hoveredCard === 'encaissements' ? 'translateY(-5px)' : 'none',
                    boxShadow: hoveredCard === 'encaissements' 
                      ? `0 20px 30px ${alpha(green[500], 0.15)}`
                      : '0 4px 12px rgba(0,0,0,0.05)',
                    position: 'relative',
                    overflow: 'hidden',
                    '&::before': {
                      content: '""',
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      height: 3,
                      background: `linear-gradient(90deg, ${green[500]}, ${green[300]})`
                    }
                  }}
                >
                  <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                      <Typography variant="caption" sx={{ color: green[700], textTransform: 'uppercase', letterSpacing: 1, fontWeight: 600 }}>
                        Total Encaissements
                      </Typography>
                      <Avatar
                        sx={{
                          width: 48,
                          height: 48,
                          bgcolor: alpha(green[500], 0.1),
                          color: green[600]
                        }}
                      >
                        <ArrowUpward />
                      </Avatar>
                    </Box>
                    
                    {loading || isRefreshing ? (
                      <Skeleton variant="text" height={40} width={120} />
                    ) : (
                      <>
                        <Typography variant="h4" sx={{ color: green[700], fontWeight: 700, mb: 1 }}>
                          {formatNumber(totalEncaissements)}
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Chip
                            size="small"
                            label={`${encaissements.length} transactions`}
                            sx={{ bgcolor: alpha(green[500], 0.1), color: green[700] }}
                          />
                          <Typography variant="caption" sx={{ color: '#5F6B7A' }}>
                            ce mois
                          </Typography>
                        </Box>
                      </>
                    )}
                  </CardContent>
                </Card>
              </motion.div>
            </Grid>

            {/* Décaissements Card */}
            <Grid item xs={12} sm={6} md={3}>
              <motion.div variants={fadeInUp}>
                <Card
                  onMouseEnter={() => setHoveredCard('decaissements')}
                  onMouseLeave={() => setHoveredCard(null)}
                  sx={{
                    height: '100%',
                    borderRadius: 3,
                    background: 'linear-gradient(135deg, #FFFFFF 0%, #F8F9FA 100%)',
                    border: `1px solid ${alpha(red[500], 0.2)}`,
                    transition: 'all 0.3s ease',
                    transform: hoveredCard === 'decaissements' ? 'translateY(-5px)' : 'none',
                    boxShadow: hoveredCard === 'decaissements' 
                      ? `0 20px 30px ${alpha(red[500], 0.15)}`
                      : '0 4px 12px rgba(0,0,0,0.05)',
                    position: 'relative',
                    overflow: 'hidden',
                    '&::before': {
                      content: '""',
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      height: 3,
                      background: `linear-gradient(90deg, ${red[500]}, ${red[300]})`
                    }
                  }}
                >
                  <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                      <Typography variant="caption" sx={{ color: red[700], textTransform: 'uppercase', letterSpacing: 1, fontWeight: 600 }}>
                        Total Décaissements
                      </Typography>
                      <Avatar
                        sx={{
                          width: 48,
                          height: 48,
                          bgcolor: alpha(red[500], 0.1),
                          color: red[600]
                        }}
                      >
                        <ArrowDownward />
                      </Avatar>
                    </Box>
                    
                    {loading || isRefreshing ? (
                      <Skeleton variant="text" height={40} width={120} />
                    ) : (
                      <>
                        <Typography variant="h4" sx={{ color: red[700], fontWeight: 700, mb: 1 }}>
                          {formatNumber(totalDecaissements)}
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Chip
                            size="small"
                            label={`${decaissements.length} transactions`}
                            sx={{ bgcolor: alpha(red[500], 0.1), color: red[700] }}
                          />
                          <Typography variant="caption" sx={{ color: '#5F6B7A' }}>
                            ce mois
                          </Typography>
                        </Box>
                      </>
                    )}
                  </CardContent>
                </Card>
              </motion.div>
            </Grid>

            {/* Solde Réel Card */}
            <Grid item xs={12} sm={6} md={3}>
              <motion.div variants={fadeInUp}>
                <Card
                  onMouseEnter={() => setHoveredCard('solde')}
                  onMouseLeave={() => setHoveredCard(null)}
                  sx={{
                    height: '100%',
                    borderRadius: 3,
                    background: solde >= 0 
                      ? 'linear-gradient(135deg, #FFFFFF 0%, #F8F9FA 100%)'
                      : 'linear-gradient(135deg, #FFF5F5 0%, #FFFFFF 100%)',
                    border: `1px solid ${solde >= 0 ? alpha(blue[500], 0.2) : alpha(red[500], 0.2)}`,
                    transition: 'all 0.3s ease',
                    transform: hoveredCard === 'solde' ? 'translateY(-5px)' : 'none',
                    boxShadow: hoveredCard === 'solde' 
                      ? `0 20px 30px ${alpha(blue[500], 0.15)}`
                      : '0 4px 12px rgba(0,0,0,0.05)',
                    position: 'relative',
                    overflow: 'hidden',
                    '&::before': {
                      content: '""',
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      height: 3,
                      background: solde >= 0 
                        ? `linear-gradient(90deg, ${blue[500]}, ${blue[300]})`
                        : `linear-gradient(90deg, ${red[500]}, ${red[300]})`
                    }
                  }}
                >
                  <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                      <Typography variant="caption" sx={{ color: solde >= 0 ? blue[700] : red[700], textTransform: 'uppercase', letterSpacing: 1, fontWeight: 600 }}>
                        Solde Réel
                      </Typography>
                      <Avatar
                        sx={{
                          width: 48,
                          height: 48,
                          bgcolor: solde >= 0 ? alpha(blue[500], 0.1) : alpha(red[500], 0.1),
                          color: solde >= 0 ? blue[600] : red[600]
                        }}
                      >
                        <AccountBalanceWallet />
                      </Avatar>
                    </Box>
                    
                    {loading || isRefreshing ? (
                      <Skeleton variant="text" height={40} width={120} />
                    ) : (
                      <>
                        <Typography variant="h4" sx={{ color: solde >= 0 ? blue[700] : red[700], fontWeight: 700, mb: 1 }}>
                          {formatNumber(solde)}
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Chip
                            size="small"
                            icon={solde >= 0 ? <TrendingUp /> : <TrendingDown />}
                            label={solde >= 0 ? 'Positif' : 'Négatif'}
                            sx={{ 
                              bgcolor: solde >= 0 ? alpha(blue[500], 0.1) : alpha(red[500], 0.1),
                              color: solde >= 0 ? blue[700] : red[700]
                            }}
                          />
                        </Box>
                      </>
                    )}
                  </CardContent>
                </Card>
              </motion.div>
            </Grid>

            {/* Chiffre Théorique Card */}
            <Grid item xs={12} sm={6} md={3}>
              <motion.div variants={fadeInUp}>
                <Card
                  onMouseEnter={() => setHoveredCard('theorique')}
                  onMouseLeave={() => setHoveredCard(null)}
                  sx={{
                    height: '100%',
                    borderRadius: 3,
                    background: 'linear-gradient(135deg, #FFFFFF 0%, #F8F9FA 100%)',
                    border: `1px solid ${alpha(orange[500], 0.2)}`,
                    transition: 'all 0.3s ease',
                    transform: hoveredCard === 'theorique' ? 'translateY(-5px)' : 'none',
                    boxShadow: hoveredCard === 'theorique' 
                      ? `0 20px 30px ${alpha(orange[500], 0.15)}`
                      : '0 4px 12px rgba(0,0,0,0.05)',
                    position: 'relative',
                    overflow: 'hidden',
                    '&::before': {
                      content: '""',
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      height: 3,
                      background: `linear-gradient(90deg, ${orange[500]}, ${orange[300]})`
                    }
                  }}
                >
                  <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                      <Typography variant="caption" sx={{ color: orange[700], textTransform: 'uppercase', letterSpacing: 1, fontWeight: 600 }}>
                        CA Théorique
                      </Typography>
                      <Avatar
                        sx={{
                          width: 48,
                          height: 48,
                          bgcolor: alpha(orange[500], 0.1),
                          color: orange[600]
                        }}
                      >
                        <ShowChart />
                      </Avatar>
                    </Box>
                    
                    {loading || isRefreshing ? (
                      <Skeleton variant="text" height={40} width={120} />
                    ) : (
                      <>
                        <Typography variant="h4" sx={{ color: orange[700], fontWeight: 700, mb: 1 }}>
                          {formatNumber(totalRapportX)}
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Chip
                            size="small"
                            label={`Écart: ${formatNumber(variance)}`}
                            sx={{ 
                              bgcolor: variance >= 0 ? alpha(green[500], 0.1) : alpha(red[500], 0.1),
                              color: variance >= 0 ? green[700] : red[700]
                            }}
                          />
                          <Typography variant="caption" sx={{ color: '#5F6B7A' }}>
                            {variancePercentage}%
                          </Typography>
                        </Box>
                      </>
                    )}
                  </CardContent>
                </Card>
              </motion.div>
            </Grid>
          </Grid>

          {/* Quick Actions */}
          <motion.div variants={fadeInUp}>
            <Paper
              sx={{
                p: 3,
                mb: 4,
                borderRadius: 3,
                background: '#FFFFFF',
                border: '1px solid #E0E0E0',
              }}
            >
              <Typography variant="h6" sx={{ color: '#1A2B3C', fontWeight: 600, mb: 2 }}>
                Actions Rapides
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={4}>
                  <Button
                    component={Link}
                    to="/Encaissement"
                    fullWidth
                    variant="contained"
                    startIcon={<AddCircle />}
                    sx={{
                      py: 1.5,
                      background: 'linear-gradient(45deg, #2E7D32, #4CAF50)',
                      borderRadius: 2,
                      textTransform: 'none',
                      fontSize: '1rem',
                      '&:hover': {
                        transform: 'translateY(-2px)',
                        boxShadow: '0 10px 20px rgba(46,125,50,0.3)'
                      }
                    }}
                  >
                    Nouvel Encaissement
                  </Button>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Button
                    component={Link}
                    to="/Decaissement"
                    fullWidth
                    variant="contained"
                    startIcon={<AddCircle />}
                    sx={{
                      py: 1.5,
                      background: 'linear-gradient(45deg, #D32F2F, #F44336)',
                      borderRadius: 2,
                      textTransform: 'none',
                      fontSize: '1rem',
                      '&:hover': {
                        transform: 'translateY(-2px)',
                        boxShadow: '0 10px 20px rgba(211,47,47,0.3)'
                      }
                    }}
                  >
                    Nouveau Décaissement
                  </Button>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Button
                    component={Link}
                    to="/AllVault"
                    fullWidth
                    variant="contained"
                    startIcon={<History />}
                    sx={{
                      py: 1.5,
                      background: 'linear-gradient(45deg, #1976D2, #2196F3)',
                      borderRadius: 2,
                      textTransform: 'none',
                      fontSize: '1rem',
                      '&:hover': {
                        transform: 'translateY(-2px)',
                        boxShadow: '0 10px 20px rgba(25,118,210,0.3)'
                      }
                    }}
                  >
                    Historique Complet
                  </Button>
                </Grid>
              </Grid>
            </Paper>
          </motion.div>

          {/* Filters Section */}
          <motion.div variants={fadeInUp}>
            <Paper
              sx={{
                p: 3,
                mb: 4,
                borderRadius: 3,
                background: '#FFFFFF',
                border: '1px solid #E0E0E0',
              }}
            >
              <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 3 }}>
                <FilterList sx={{ color: '#1976D2' }} />
                <Typography variant="h6" sx={{ color: '#1A2B3C', fontWeight: 600 }}>
                  Filtres
                </Typography>
                {(dateDebut || dateFin || filterType) && (
                  <Chip
                    label="Filtres actifs"
                    onDelete={clearFilters}
                    size="small"
                    sx={{ bgcolor: '#E3F2FD', color: '#1976D2' }}
                  />
                )}
              </Stack>

              <Grid container spacing={2}>
                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    fullWidth
                    label="Date de début"
                    type="date"
                    value={dateDebut}
                    onChange={(e) => setDateDebut(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    variant="outlined"
                    size="small"
                    InputProps={{
                      startAdornment: (
                        <DateRange sx={{ color: '#9AA6B2', mr: 1, fontSize: 18 }} />
                      ),
                    }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        bgcolor: '#F5F7FA',
                        '&:hover .MuiOutlinedInput-notchedOutline': {
                          borderColor: '#1976D2'
                        }
                      }
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    fullWidth
                    label="Date de fin"
                    type="date"
                    value={dateFin}
                    onChange={(e) => setDateFin(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    variant="outlined"
                    size="small"
                    InputProps={{
                      startAdornment: (
                        <DateRange sx={{ color: '#9AA6B2', mr: 1, fontSize: 18 }} />
                      ),
                    }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        bgcolor: '#F5F7FA',
                        '&:hover .MuiOutlinedInput-notchedOutline': {
                          borderColor: '#1976D2'
                        }
                      }
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <FormControl fullWidth size="small">
                    <Select
                      value={filterType}
                      onChange={(e) => setFilterType(e.target.value)}
                      displayEmpty
                      sx={{
                        bgcolor: '#F5F7FA',
                        '& .MuiOutlinedInput-notchedOutline': {
                          borderColor: '#E0E0E0'
                        },
                        '&:hover .MuiOutlinedInput-notchedOutline': {
                          borderColor: '#1976D2'
                        }
                      }}
                    >
                      <MenuItem value="">Tous les types</MenuItem>
                      <MenuItem value="ENCAISSEMENT">Encaissements</MenuItem>
                      <MenuItem value="DECAISSEMENT">Décaissements</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Button
                    fullWidth
                    variant="outlined"
                    onClick={clearFilters}
                    startIcon={<Clear />}
                    sx={{
                      height: 40,
                      borderColor: '#E0E0E0',
                      color: '#5F6B7A',
                      '&:hover': {
                        borderColor: '#1976D2',
                        bgcolor: '#E3F2FD'
                      }
                    }}
                  >
                    Réinitialiser
                  </Button>
                </Grid>
              </Grid>

              {/* Active Filters Display */}
              {(dateDebut || dateFin || filterType) && (
                <Fade in={true}>
                  <Box sx={{ mt: 2, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    {dateDebut && (
                      <Chip
                        label={`Du: ${new Date(dateDebut).toLocaleDateString('fr-FR')}`}
                        onDelete={() => setDateDebut('')}
                        size="small"
                        sx={{ bgcolor: '#E8F5E9', color: '#2E7D32' }}
                      />
                    )}
                    {dateFin && (
                      <Chip
                        label={`Au: ${new Date(dateFin).toLocaleDateString('fr-FR')}`}
                        onDelete={() => setDateFin('')}
                        size="small"
                        sx={{ bgcolor: '#FFEBEE', color: '#D32F2F' }}
                      />
                    )}
                    {filterType && (
                      <Chip
                        label={filterType === "ENCAISSEMENT" ? "Encaissements" : "Décaissements"}
                        onDelete={() => setFilterType('')}
                        size="small"
                        sx={{ 
                          bgcolor: filterType === "ENCAISSEMENT" ? '#E8F5E9' : '#FFEBEE',
                          color: filterType === "ENCAISSEMENT" ? '#2E7D32' : '#D32F2F'
                        }}
                      />
                    )}
                  </Box>
                </Fade>
              )}
            </Paper>
          </motion.div>

          {/* Data Table Section */}
          <motion.div variants={fadeInUp}>
            <Paper
              sx={{
                p: 3,
                borderRadius: 3,
                background: '#FFFFFF',
                border: '1px solid #E0E0E0',
                overflow: 'hidden'
              }}
            >
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Typography variant="h6" sx={{ color: '#1A2B3C', fontWeight: 600 }}>
                    Liste des transactions
                  </Typography>
                  <Badge
                    badgeContent={filteredData.length}
                    color="primary"
                    sx={{
                      '& .MuiBadge-badge': {
                        bgcolor: '#1976D2',
                        color: '#FFFFFF'
                      }
                    }}
                  />
                </Box>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Chip
                    icon={<ArrowUpward />}
                    label={`Encaissements: ${filteredData.filter(d => d.category === 'ENCAISSEMENT').length}`}
                    sx={{ bgcolor: alpha(green[500], 0.1), color: green[700] }}
                  />
                  <Chip
                    icon={<ArrowDownward />}
                    label={`Décaissements: ${filteredData.filter(d => d.category === 'DECAISSEMENT').length}`}
                    sx={{ bgcolor: alpha(red[500], 0.1), color: red[700] }}
                  />
                </Box>
              </Box>

              {loading ? (
                <Box sx={{ p: 4 }}>
                  <LinearProgress sx={{ mb: 2, borderRadius: 1 }} />
                  <Skeleton variant="rectangular" height={400} sx={{ borderRadius: 2 }} />
                </Box>
              ) : (
                <>
                  <div style={{ overflowX: "auto" }}>
                    <ExcelTable data={filteredData} />
                  </div>

                  {filteredData.length === 0 && (
                    <Fade in={true}>
                      <Box sx={{ py: 8, textAlign: 'center' }}>
                        <Receipt sx={{ fontSize: 60, color: '#B0BEC5', mb: 2 }} />
                        <Typography variant="h6" sx={{ color: '#5F6B7A' }}>
                          Aucune transaction trouvée
                        </Typography>
                        <Typography variant="body2" sx={{ color: '#9AA6B2' }}>
                          Essayez de modifier vos filtres de recherche
                        </Typography>
                        <Button
                          variant="outlined"
                          onClick={clearFilters}
                          sx={{ mt: 2 }}
                        >
                          Réinitialiser les filtres
                        </Button>
                      </Box>
                    </Fade>
                  )}
                </>
              )}
            </Paper>
          </motion.div>

          {/* Footer Stats */}
          <motion.div variants={fadeInUp}>
            <Box sx={{ mt: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="caption" sx={{ color: '#9AA6B2' }}>
                Dernière mise à jour: {lastUpdate.toLocaleDateString('fr-FR')} à {lastUpdate.toLocaleTimeString('fr-FR')}
              </Typography>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <Chip
                  icon={<Speed />}
                  label={`${filteredData.length} transactions affichées`}
                  size="small"
                  sx={{ bgcolor: '#F5F7FA', color: '#5F6B7A' }}
                />
                <Chip
                  icon={<Dashboard />}
                  label={`${new Set(filteredData.map(d => d.type)).size} types`}
                  size="small"
                  sx={{ bgcolor: '#F5F7FA', color: '#5F6B7A' }}
                />
              </Box>
            </Box>
          </motion.div>
        </motion.div>
      </Container>
    </Box>
  );
};

export default VaultPage;